/**
 * useRealtimeArbitrage - Real-time arbitrage opportunity streaming via WebSocket
 * Streams cross-exchange price differentials, opportunity alerts, and execution updates
 */

'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { useWebSocket } from '@/domains/market-data/hooks/useWebSocket';

// =============================================================================
// TYPES
// =============================================================================

export interface RealtimeArbitrageOpportunity {
  opportunityId: string;
  symbol: string;
  buyExchange: string;
  sellExchange: string;
  buyPrice: number;
  sellPrice: number;
  spread: number;
  spreadPercent: number;
  estimatedProfit: number;
  estimatedProfitPercent: number;
  maxVolume: number;
  fees: number;
  netProfit: number;
  confidence: number;
  expiresIn: number; // milliseconds
  timestamp: string;
}

export interface RealtimeArbitrageExecution {
  executionId: string;
  opportunityId: string;
  symbol: string;
  status: 'pending' | 'buying' | 'selling' | 'completed' | 'failed' | 'partial';
  buyExchange: string;
  sellExchange: string;
  buyPrice: number;
  sellPrice: number;
  quantity: number;
  realizedProfit?: number;
  fees?: number;
  errorMessage?: string;
  startedAt: string;
  completedAt?: string;
}

export interface ExchangeLatency {
  exchange: string;
  latency: number;
  status: 'fast' | 'normal' | 'slow' | 'timeout';
  timestamp: string;
}

export interface UseRealtimeArbitrageConfig {
  /** Auto-connect on mount */
  autoConnect?: boolean;
  /** Maximum opportunities to buffer */
  maxOpportunities?: number;
  /** Minimum spread percent to show */
  minSpreadPercent?: number;
  /** Minimum estimated profit */
  minProfit?: number;
  /** Filter by symbols */
  symbols?: string[];
  /** Callback on new opportunity */
  onOpportunity?: (opportunity: RealtimeArbitrageOpportunity) => void;
  /** Callback on execution update */
  onExecutionUpdate?: (execution: RealtimeArbitrageExecution) => void;
}

// =============================================================================
// HOOK
// =============================================================================

export const useRealtimeArbitrage = (config: UseRealtimeArbitrageConfig = {}) => {
  const {
    autoConnect = true,
    maxOpportunities = 50,
    minSpreadPercent = 0,
    minProfit = 0,
    symbols,
    onOpportunity,
    onExecutionUpdate,
  } = config;

  const [opportunities, setOpportunities] = useState<Map<string, RealtimeArbitrageOpportunity>>(new Map());
  const [activeExecutions, setActiveExecutions] = useState<Map<string, RealtimeArbitrageExecution>>(new Map());
  const [completedExecutions, setCompletedExecutions] = useState<RealtimeArbitrageExecution[]>([]);
  const [exchangeLatencies, setExchangeLatencies] = useState<Map<string, ExchangeLatency>>(new Map());
  const [opportunityCount, setOpportunityCount] = useState(0);

  const callbacksRef = useRef({ onOpportunity, onExecutionUpdate });
  callbacksRef.current = { onOpportunity, onExecutionUpdate };

  // Expire stale opportunities
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setOpportunities((prev: Map<string, RealtimeArbitrageOpportunity>) => {
        const updated = new Map(prev);
        let changed = false;
        for (const [id, opp] of updated) {
          const createdAt = new Date(opp.timestamp).getTime();
          if (now - createdAt > opp.expiresIn) {
            updated.delete(id);
            changed = true;
          }
        }
        return changed ? updated : prev;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleMessage = useCallback((data: any) => {
    if (data.type === 'pong' || data.type === 'ping') return;

    switch (data.type) {
      case 'arbitrage_opportunity': {
        const opp = data.data as RealtimeArbitrageOpportunity;

        // Filter by symbols
        if (symbols?.length && !symbols.includes(opp.symbol)) return;

        // Filter by minimum spread
        if (opp.spreadPercent < minSpreadPercent) return;

        // Filter by minimum profit
        if (opp.estimatedProfit < minProfit) return;

        setOpportunities((prev: Map<string, RealtimeArbitrageOpportunity>) => {
          const updated = new Map(prev);
          updated.set(opp.opportunityId, opp);

          // Trim to max
          if (updated.size > maxOpportunities) {
            const sortedEntries = Array.from(updated.entries()) as [string, RealtimeArbitrageOpportunity][];
            sortedEntries.sort((a, b) => b[1].spreadPercent - a[1].spreadPercent);
            return new Map(sortedEntries.slice(0, maxOpportunities));
          }

          return updated;
        });

        setOpportunityCount((prev: number) => prev + 1);
        callbacksRef.current.onOpportunity?.(opp);
        break;
      }

      case 'arbitrage_execution': {
        const exec = data.data as RealtimeArbitrageExecution;

        if (['completed', 'failed'].includes(exec.status)) {
          setActiveExecutions((prev: Map<string, RealtimeArbitrageExecution>) => {
            const updated = new Map(prev);
            updated.delete(exec.executionId);
            return updated;
          });
          setCompletedExecutions((prev: RealtimeArbitrageExecution[]) => [exec, ...prev].slice(0, 100));
        } else {
          setActiveExecutions((prev: Map<string, RealtimeArbitrageExecution>) => {
            const updated = new Map(prev);
            updated.set(exec.executionId, exec);
            return updated;
          });
        }

        callbacksRef.current.onExecutionUpdate?.(exec);
        break;
      }

      case 'exchange_latency': {
        const latency = data.data as ExchangeLatency;
        setExchangeLatencies((prev: Map<string, ExchangeLatency>) => {
          const updated = new Map(prev);
          updated.set(latency.exchange, latency);
          return updated;
        });
        break;
      }
    }
  }, [symbols, minSpreadPercent, minProfit, maxOpportunities]);

  // Public market stream: ticket optional (soft-fail auth → open without ticket)
  const { state, send, connect, disconnect, subscribe } = useWebSocket(
    {
      channel: 'market-data',
      useTicket: true,
      requireTicket: false,
      softFailAuth: true,
      reconnect: { enabled: true, maxAttempts: 10, delay: 1000, backoffMultiplier: 1.5 },
      heartbeat: { enabled: true, interval: 15000, message: { type: 'ping' } },
    },
    handleMessage
  );

  // Auto-connect
  useEffect(() => {
    if (autoConnect) {
      connect();
    }
    return () => disconnect();
  }, [autoConnect, connect, disconnect]);

  // Subscribe to arbitrage channels when connected
  useEffect(() => {
    if (state.status === 'connected') {
      subscribe('arbitrage_opportunity', () => { });
      subscribe('arbitrage_execution', () => { });
      subscribe('exchange_latency', () => { });

      send({
        type: 'subscribe',
        channels: ['arbitrage_opportunities', 'arbitrage_executions', 'exchange_latency'],
        ...(symbols?.length ? { symbols } : {}),
      });
    }
  }, [state.status, subscribe, send, symbols]);

  // Derived state
  const opportunitiesArray = (Array.from(opportunities.values()) as RealtimeArbitrageOpportunity[])
    .sort((a: RealtimeArbitrageOpportunity, b: RealtimeArbitrageOpportunity) => b.spreadPercent - a.spreadPercent);

  const topOpportunities = opportunitiesArray.slice(0, 10);
  const activeExecutionsArray = Array.from(activeExecutions.values()) as RealtimeArbitrageExecution[];
  const latenciesArray = Array.from(exchangeLatencies.values()) as ExchangeLatency[];
  const totalProfit = completedExecutions
    .filter((e: RealtimeArbitrageExecution) => e.status === 'completed' && e.realizedProfit)
    .reduce((sum: number, e: RealtimeArbitrageExecution) => sum + (e.realizedProfit || 0), 0);

  const clearHistory = useCallback(() => {
    setCompletedExecutions([]);
    setOpportunityCount(0);
  }, []);

  return {
    // Connection
    connectionStatus: state.status,
    isConnected: state.status === 'connected',
    connect,
    disconnect,

    // Data
    opportunities: opportunitiesArray,
    topOpportunities,
    activeExecutions: activeExecutionsArray,
    completedExecutions,
    exchangeLatencies: latenciesArray,
    opportunityCount,
    totalProfit,

    // Actions
    clearHistory,
    send,
  };
};

export default useRealtimeArbitrage;
